import { styled, Tabs, Tab, TabProps } from "@mui/material";
import Box from "@mui/material/Box";

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

interface StyledTabsProps {
  children?: React.ReactNode;
  value: string;
  onChange: (event: React.SyntheticEvent, newValue: string) => void;
}

export const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))(({ theme }) => ({
  backgroundColor: "inherit",
  "& .MuiTabs-indicator": {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
    height: "3.5px",
  },
  "& .MuiTabs-indicatorSpan": {
    width: "100%",
    backgroundColor: theme.palette.primary.main,
    borderTopLeftRadius: "4px",
    borderTopRightRadius: "4px",
  },
  "& .MuiTabs-flexContainer": {
    height: "48px",
    gap: "32px",
  },
}));

export const StyledTab = styled((props: TabProps) => <Tab {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    color: theme.palette.text.secondary,
    padding: 0,
    minWidth: "auto",
    "&.Mui-selected": {
      color: theme.palette.primary.main,
      fontWeight: "medium",
    },
  }),
);

export const MainTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2.5 }}>{children}</Box>}
    </div>
  );
};

export const StudyDesignerTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: "40px 0px" }}>{children}</Box>}
    </div>
  );
};

export const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

type NumberedTabLabelProps = {
  number: number;
  label: React.ReactNode;
  active?: boolean;
};

export const NumberedTabLabel = ({
  number,
  label,
  active,
}: NumberedTabLabelProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <Box
      sx={{
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: "12px",
        fontWeight: 600,
        border: active ? "none" : "1.5px solid",
        borderColor: "text.secondary",
        bgcolor: active ? "primary.main" : "transparent",
        color: active ? "primary.contrastText" : "text.secondary",
      }}
    >
      {number}
    </Box>
    {label}
  </Box>
);

export const ModalTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1 }}>{children}</Box>}
    </div>
  );
};
